import * as React from 'react';
import './FilterChipSelect.scss';

export interface FilterChipOption {
  value: string;
  label: string;
  color?: string;
  group?: string;
  groupHint?: string;
  order?: number;
}

interface Props {
  label: string;
  options: FilterChipOption[];
  selected: string[];
  onToggle: (value: string) => void;
  searchable?: boolean;
  searchPlaceholder?: string;
  collapsedLimit?: number;
  disabledValues?: string[];
  hint?: React.ReactNode;
  className?: string;
}

export const FilterChipSelect: React.FC<Props> = ({
  label,
  options,
  selected,
  onToggle,
  searchable = false,
  searchPlaceholder = 'Search',
  collapsedLimit,
  disabledValues = [],
  hint,
  className = ''
}) => {
  const [search, setSearch] = React.useState('');
  const [isExpanded, setIsExpanded] = React.useState(false);
  const query = search.trim().toLocaleLowerCase();
  const selectedValues = React.useMemo(() => new Set(selected.map(String)), [selected]);
  const disabled = React.useMemo(() => new Set(disabledValues.map(String)), [disabledValues]);
  const collator = React.useMemo(() => new Intl.Collator('en-GB', { numeric: true, sensitivity: 'base' }), []);
  const sortedOptions = options
    .filter(option => !query || option.label.toLocaleLowerCase().includes(query))
    .slice()
    .sort((left, right) => (left.order || 0) - (right.order || 0) || collator.compare(left.label, right.label));
  const canCollapse = !query && typeof collapsedLimit === 'number' && collapsedLimit > 0;
  const orderedOptions = canCollapse
    ? sortedOptions.slice().sort((left, right) => {
        const selectedOrder =
          Number(selectedValues.has(String(right.value))) - Number(selectedValues.has(String(left.value)));
        return selectedOrder || sortedOptions.indexOf(left) - sortedOptions.indexOf(right);
      })
    : sortedOptions;
  const visibleOptions = canCollapse && !isExpanded ? orderedOptions.slice(0, collapsedLimit) : orderedOptions;
  const hiddenCount = canCollapse && !isExpanded ? Math.max(orderedOptions.length - visibleOptions.length, 0) : 0;
  const groups = visibleOptions.reduce((result, option) => {
    const key = option.group || '';
    if (!result[key]) result[key] = [];
    result[key].push(option);
    return result;
  }, {} as Record<string, FilterChipOption[]>);

  const renderOptions = (items: FilterChipOption[], includeExpansionControl: boolean) => (
    <div className="people-chipFilter__chips">
      {items.map(option => {
        const value = String(option.value);
        const active = selectedValues.has(value);
        const style = option.color
          ? ({
              color: active ? '#fff' : option.color,
              borderColor: active ? option.color : `${option.color}66`,
              backgroundColor: active ? option.color : `${option.color}12`
            } as React.CSSProperties)
          : undefined;
        return (
          <button
            type="button"
            key={value}
            className={active ? 'people-chipFilter__chip is-selected' : 'people-chipFilter__chip'}
            style={style}
            aria-pressed={active}
            disabled={disabled.has(value)}
            onClick={event => {
              event.stopPropagation();
              onToggle(value);
            }}
          >
            {active && <span aria-hidden="true">✓</span>}
            {option.label}
          </button>
        );
      })}
      {includeExpansionControl && hiddenCount > 0 && (
        <button
          type="button"
          className="people-chipFilter__more"
          aria-expanded="false"
          onClick={event => {
            event.stopPropagation();
            setIsExpanded(true);
          }}
        >
          +{hiddenCount}
        </button>
      )}
      {includeExpansionControl && canCollapse && isExpanded && orderedOptions.length > Number(collapsedLimit) && (
        <button
          type="button"
          className="people-chipFilter__more"
          aria-expanded="true"
          onClick={event => {
            event.stopPropagation();
            setIsExpanded(false);
          }}
        >
          Show less
        </button>
      )}
    </div>
  );

  return (
    <fieldset className={`people-chipFilter ${className}`.trim()}>
      <legend>{label}</legend>
      {hint}
      {searchable && (
        <input
          type="search"
          value={search}
          placeholder={searchPlaceholder}
          onChange={event => setSearch(event.target.value)}
        />
      )}
      <div className="people-chipFilter__body">
        {Object.keys(groups).map((group, index, groupNames) => (
          <section className="people-chipFilter__group" key={group || 'options'}>
            {group && (
              <div className="people-chipFilter__groupTitle">
                <strong>{group}</strong>
                <span>{groups[group][0].groupHint}</span>
              </div>
            )}
            {renderOptions(groups[group], index === groupNames.length - 1)}
          </section>
        ))}
        {visibleOptions.length === 0 && <span className="people-chipFilter__empty">No matches</span>}
      </div>
    </fieldset>
  );
};
